import React, { createContext, useContext } from "react";

import { useSession } from "next-auth/react";

import { api } from "@musicfy/utils";
import { useArray } from "@musicfy/utils/hooks";
import {
  type IUseArrayUpdateIndex,
  type IUseArrayAppend,
  type IUseArrayRemoveIndex,
} from "@musicfy/utils/hooks/useArray";

import { type TSongGeneration } from "./ISongGenerations";

interface ISongGenerationsContext {
  songGenerations: TSongGeneration[];
  updateSongGenerationAtIndex: IUseArrayUpdateIndex<
    TSongGeneration,
    TSongGeneration[]
  >;
  appendSongGeneration: IUseArrayAppend<TSongGeneration, TSongGeneration[]>;
  removeSongGenerationAtIndex: IUseArrayRemoveIndex<TSongGeneration[]>;
  setSongGenerations: React.Dispatch<React.SetStateAction<TSongGeneration[]>>;
  isSongGenerationsLoading: boolean;
}

const SongGenerationsContext = createContext<ISongGenerationsContext | null>(
  null
);

export function useSongGenerationsContext() {
  const songGenerationsContext = useContext(SongGenerationsContext);

  if (!songGenerationsContext) {
    throw new Error(
      "useSongGenerationsContext must be used within a SongGenerationsProvider"
    );
  }

  return songGenerationsContext;
}

const SongGenerationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { status } = useSession();
  const {
    value: songGenerations,
    setValue: setSongGenerations,
    updateIndex: updateSongGenerationAtIndex,
    removeIndex: removeSongGenerationAtIndex,
    append: appendSongGeneration,
  } = useArray<TSongGeneration>([]);

  const { isFetching: isSongGenerationsLoading } =
    api.songGenerations.getSongGenerationsForUser.useQuery(undefined, {
      onSuccess: (data) => {
        if (!data) {
          return;
        }

        setSongGenerations(data);
      },
      retry: false,
      enabled: status === "authenticated",
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      refetchInterval: () =>
        songGenerations.some((g) => g.status === "processing") ? 5000 : false,
      refetchIntervalInBackground: true,
    });

  const songGenerationsContextValue = {
    songGenerations,
    updateSongGenerationAtIndex,
    appendSongGeneration,
    removeSongGenerationAtIndex,
    setSongGenerations,
    isSongGenerationsLoading,
  };

  return (
    <SongGenerationsContext.Provider value={songGenerationsContextValue}>
      {children}
    </SongGenerationsContext.Provider>
  );
};

export default SongGenerationsProvider;
